import { useEffect, useRef } from "react";
import * as THREE from "three";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const COUNT = 8000;

function sampleSegments(segs, count, out, offset, jitter = 0.05) {
  const lens = segs.map(([a, b]) => Math.hypot(b[0] - a[0], b[1] - a[1]));
  const total = lens.reduce((s, l) => s + l, 0);
  for (let i = 0; i < count; i++) {
    let d = Math.random() * total;
    let j = 0;
    while (d > lens[j] && j < segs.length - 1) { d -= lens[j]; j++; }
    const [a, b] = segs[j];
    const t = d / lens[j];
    const idx = (offset + i) * 3;
    out[idx] = a[0] + (b[0] - a[0]) * t + (Math.random() - 0.5) * jitter;
    out[idx + 1] = a[1] + (b[1] - a[1]) * t + (Math.random() - 0.5) * jitter;
    out[idx + 2] = (Math.random() - 0.5) * 0.5;
  }
}

function compassPositions() {
  const out = new Float32Array(COUNT * 3);
  const o = 2.5, n = 0.34;
  const v = [
    [0, o], [n, n], [o, 0], [n, -n], [0, -o], [-n, -n], [-o, 0], [-n, n],
  ];
  const segs = v.map((p, i) => [p, v[(i + 1) % v.length]]);
  const starCount = Math.floor(COUNT * 0.68);
  sampleSegments(segs, starCount, out, 0);
  const ringCount = COUNT - starCount;
  for (let i = 0; i < ringCount; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = 2.95 + (Math.random() - 0.5) * 0.07;
    const idx = (starCount + i) * 3;
    out[idx] = Math.cos(a) * r;
    out[idx + 1] = Math.sin(a) * r;
    out[idx + 2] = (Math.random() - 0.5) * 0.5;
  }
  return out;
}

function housePositions() {
  const out = new Float32Array(COUNT * 3);
  const segs = [
    [[-1.9, -1.7], [1.9, -1.7]],
    [[1.9, -1.7], [1.9, 0.5]],
    [[-1.9, -1.7], [-1.9, 0.5]],
    [[-2.25, 0.5], [0, 2.35]],
    [[2.25, 0.5], [0, 2.35]],
    [[-2.25, 0.5], [-1.9, 0.5]],
    [[1.9, 0.5], [2.25, 0.5]],
    [[-0.45, -1.7], [-0.45, -0.45]],
    [[-0.45, -0.45], [0.45, -0.45]],
    [[0.45, -0.45], [0.45, -1.7]],
    [[0.85, -1.0], [1.5, -1.0]],
    [[1.5, -1.0], [1.5, -0.35]],
    [[1.5, -0.35], [0.85, -0.35]],
    [[0.85, -0.35], [0.85, -1.0]],
    [[-1.5, -1.0], [-0.85, -1.0]],
    [[-0.85, -1.0], [-0.85, -0.35]],
    [[-0.85, -0.35], [-1.5, -0.35]],
    [[-1.5, -0.35], [-1.5, -1.0]],
  ];
  sampleSegments(segs, COUNT, out, 0);
  return out;
}

function scatterPositions(spread) {
  const out = new Float32Array(COUNT * 3);
  for (let i = 0; i < COUNT * 3; i += 3) {
    out[i] = (Math.random() - 0.5) * spread;
    out[i + 1] = (Math.random() - 0.5) * spread;
    out[i + 2] = (Math.random() - 0.5) * spread * 0.55;
  }
  return out;
}

const ease = (t) => t * t * (3 - 2 * t);

export default function ParticleHero() {
  const mountRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.z = 6.2;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const compass = compassPositions();
    const house = housePositions();
    const scatterA = scatterPositions(14);
    const scatterB = scatterPositions(18);

    const positions = new Float32Array(COUNT * 3);
    positions.set(scatterA);
    const colors = new Float32Array(COUNT * 3);
    const base = new THREE.Color("#0162CA");
    const phases = new Float32Array(COUNT);
    const speeds = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      const b = 0.8 + Math.random() * 0.4;
      colors[i * 3] = base.r * b;
      colors[i * 3 + 1] = base.g * b;
      colors[i * 3 + 2] = base.b * b;
      phases[i] = Math.random() * Math.PI * 2;
      speeds[i] = 0.5 + Math.random() * 1.4;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.PointsMaterial({
      size: 0.042,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    scene.add(new THREE.Points(geo, mat));

    const assemble = { v: reduced ? 1 : 0 };
    if (!reduced) gsap.to(assemble, { v: 1, duration: 1.4, ease: "power3.out", delay: 0.2 });

    let scrollP = 0;
    let st = null;
    if (!reduced) {
      st = ScrollTrigger.create({
        start: 0,
        end: () => window.innerHeight * 2.2,
        onUpdate: (self) => { scrollP = self.progress; },
      });
    }

    const clock = new THREE.Clock();
    let raf = 0;
    const arr = geo.attributes.position.array;

    const animate = () => {
      const t = clock.getElapsedTime();
      const explode = ease(Math.min(Math.max(scrollP / 0.35, 0), 1));
      const gather = ease(Math.min(Math.max((scrollP - 0.35) / 0.35, 0), 1));
      const a = ease(assemble.v);
      for (let i = 0; i < COUNT; i++) {
        const ci = i * 3;
        let x, y, z;
        if (gather > 0) {
          x = scatterB[ci] + (house[ci] - scatterB[ci]) * gather;
          y = scatterB[ci + 1] + (house[ci + 1] - scatterB[ci + 1]) * gather;
          z = scatterB[ci + 2] + (house[ci + 2] - scatterB[ci + 2]) * gather;
        } else if (explode > 0) {
          const e = Math.min(explode * (0.7 + (i % 10) * 0.06), 1);
          x = compass[ci] + (scatterB[ci] - compass[ci]) * e;
          y = compass[ci + 1] + (scatterB[ci + 1] - compass[ci + 1]) * e;
          z = compass[ci + 2] + (scatterB[ci + 2] - compass[ci + 2]) * e;
        } else {
          x = scatterA[ci] + (compass[ci] - scatterA[ci]) * a;
          y = scatterA[ci + 1] + (compass[ci + 1] - scatterA[ci + 1]) * a;
          z = scatterA[ci + 2] + (compass[ci + 2] - scatterA[ci + 2]) * a;
        }
        const ph = phases[i];
        const sp = speeds[i];
        arr[ci] = x + Math.sin(t * sp + ph) * 0.035;
        arr[ci + 1] = y + Math.cos(t * sp * 0.8 + ph) * 0.035;
        arr[ci + 2] = z;
      }
      geo.attributes.position.needsUpdate = true;
      scene.rotation.z = Math.sin(t * 0.08) * 0.03;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    const onResize = () => {
      if (!mount.clientWidth) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      if (st) st.kill();
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div ref={mountRef} className="absolute inset-0 pointer-events-none" data-testid="particle-hero-canvas">
      <div className="hero-shimmer absolute inset-0 pointer-events-none" />
    </div>
  );
}
